<template>
  <div class="relative">
    <ul class="flex ml-6 space-x-2 text-xs">
      <li
        v-for="(criteria, idx) in filters"
        :key="idx"
        class="border-l border-r border-t border-gray-400 rounded-t relative"
        :class="[
          currentFilter && criteria.name === currentFilter.name
            ? currentFilterClasses
            : filterClasses,
        ]"
      >
        <a
          role="button"
          @click="setCurrentFilterAndUpdateList(criteria)"
          class="inline-block px-6 py-3 text-gray-700"
        >
          {{ $te(criteria.name) ? $t(criteria.name) : criteria.name | capitalize }}
        </a>
        <a
          v-if="criteria.custom"
          role="button"
          class="text-gray-500 hover:text-red-500 inline-block text-center pr-6 py-3"
          @click="forgetFilter(idx)"
        >
          <FontAwesomeIcon slot="button" icon="times" fixed-width />
        </a>
      </li>
      <li v-if="!loading && meta && Object.keys(meta).length" class="ml-auto py-3">
        <span class="text-gray-700"> {{ $t('totalResults') | capitalize }}: </span>
        {{ meta.total || items.length }}
      </li>
    </ul>
    <div class="rounded-lg overflow-hidden" :class="{ 'border border-gray-400': !nested }">
      <div
        v-if="searchable || bulkActionable || filterable"
        class="flex bg-white p-2 border-b border-gray-400"
      >
        <div v-if="searchable" class="w-1/2 flex items-baseline">
          <div class="w-16 text-center text-gray-500">
            <FontAwesomeIcon icon="search" fixed-width />
          </div>
          <input
            type="text"
            v-model="searchTerm"
            :disabled="false"
            class="input bg-gray-300 border-gray-400"
            placeholder="Search term ..."
            @change="onSearchChange"
          />
        </div>
        <div class="flex-none space-x-2 ml-auto">
          <button v-if="bulkActionable" class="btn btn-brand space-x-4" :disabled="!hasAnySelected">
            <span>{{ $t('bulkActions') | capitalize }}</span>
            <FontAwesomeIcon icon="ellipsis-v" />
          </button>
          <Dropdown v-if="filterable" :link="false">
            <template #button="{ open }">
              <BaseLinkButton purple @click="open">
                <FontAwesomeIcon icon="filter" fixed-width />
              </BaseLinkButton>
            </template>
            <template slot-scope="{ closeAction }">
              <slot
                name="list-filters"
                :toggle-menu="closeAction"
                :set-custom-filter="setCustomFilter"
              />
            </template>
          </Dropdown>
        </div>
      </div>
      <div v-else class="h-2 border-b border-gray-400 bg-white" />

      <div class="flex-1 bg-white">
        <div class="list list--nested">
          <DataIteratHeader
            v-if="!headless"
            :selectable="bulkActionable"
            :actionable="actionable && items.length > 0"
            @checked="onHeaderCheckboxChecked"
          >
            <slot name="list-header-content" />
          </DataIteratHeader>

          <div class="list__body">
            <div v-if="loading" class="list__item px-3 py-6 text-center">
              <Loader />
            </div>
            <DataIteratItem
              v-else-if="items.length"
              v-for="(item, idx) in items"
              :key="`${itemKeyPrefix}_${idx}`"
              :checked="itemIds[item.id] || false"
              :selectable="bulkActionable"
              :actionable="actionable"
              @checked="onItemCheckboxChecked(item, arguments[0])"
            >
              <slot name="list-item-content" :data-item="item" :data-item-index="idx">
                {{ item }}
              </slot>
              <template slot="actions" slot-scope="{ closeAction }">
                <slot name="list-item-actions" :data-item="item" :on-click="closeAction" />
              </template>
            </DataIteratItem>
            <NoResults v-else />
          </div>
        </div>
      </div>

      <div v-if="paginatable" class="flex bg-gray-200 border-t border-gray-400 p-2">
        <div class="flex-none ml-auto glue-h">
          <button
            class="btn btn-brand space-x-4 pr-5"
            :disabled="meta.current_page === 1"
            @click="onPreviousPage"
          >
            <FontAwesomeIcon icon="angle-left" />
            <span>{{ $t('previous') | capitalize }}</span>
          </button>
          <button
            class="btn btn-brand space-x-4 pl-5"
            :disabled="meta.current_page === meta.last_page"
            @click="onNextPage"
          >
            <span>{{ $t('next') | capitalize }}</span>
            <FontAwesomeIcon icon="angle-right" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DataIteratHeader from './dataIterat/Header.vue';
import DataIteratItem from './dataIterat/Item.vue';
import NoResults from './dataIterat/NoResults.vue';

const baseFilters = [
  {
    name: 'all',
    default: false,
    filters: [],
  },
];

export default {
  name: 'DataIterat',
  components: {
    DataIteratHeader,
    DataIteratItem,
    NoResults,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    meta: {
      type: Object,
      default: () => ({}),
    },
    searchFieldName: {
      type: String,
      default: 'name',
    },
    searchable: Boolean,
    filterable: Boolean,
    filterPresets: {
      type: Array,
      default: () => [],
    },
    bulkActionable: Boolean,
    actionable: Boolean,
    headless: Boolean,
    paginatable: Boolean,
    loading: {
      type: Boolean,
      default: false,
    },
    nested: Boolean,
    itemKeyPrefix: {
      type: String,
      default: 'list-item',
    },
    withBaseFilters: Boolean,
  },
  data: () => ({
    itemIds: {},
    page: 1,
    searchTerm: '',
    currentFilter: null,
    filters: [],
  }),
  computed: {
    hasAnySelected() {
      return Object.keys(this.itemIds).some((id) => this.itemIds[id] === true);
    },
    queryParams() {
      const { page, filter } = this;
      return {
        page,
        filter,
      };
    },
    currentFilterClasses() {
      return ['bg-white', '-mb-px'];
    },
    filterClasses() {
      return ['text-gray-600', 'bg-gray-400', ' hover:bg-gray-200'];
    },
  },
  watch: {
    items(newItems) {
      this.updateItemIds(newItems);
    },
    meta: {
      handler(newMeta) {
        const { current_page: currentPage = 1 } = newMeta || {};
        this.page = currentPage;
      },
      deep: true,
    },
    filterPresets: {
      handler() {
        this.setFilters();
      },
      deep: true,
    },
  },
  created() {
    const { meta = {} } = this;

    const { current_page: currentPage = 1 } = meta || {};
    this.page = currentPage;

    this.updateItemIds(this.items);
    this.setFilters();
    const defaultPreset = this.filters.find((preset) => preset.default) || this.filters[0] || null;
    this.setCurrentFilterAndUpdateList(defaultPreset);
  },
  methods: {
    setFilters() {
      this.filters = [...(this.withBaseFilters ? baseFilters : []), ...this.filterPresets];
    },
    setCurrentFilterAndUpdateList(filter = {}) {
      this.setCurrentFilter(filter);
      this.getData();
    },
    setCurrentFilter(filter) {
      this.currentFilter = { ...filter };
    },
    updateItemIds(items) {
      items.forEach((item) => {
        this.itemIds = { [item.id]: false, ...this.itemIds };
      });
    },
    onSearchChange() {
      this.getData();
    },
    onHeaderCheckboxChecked(checked) {
      if (checked) {
        this.items.forEach((item) => {
          this.itemIds[item.id] = true;
        });
      } else {
        this.items.forEach((item) => {
          this.itemIds[item.id] = false;
        });
      }
    },
    onItemCheckboxChecked(item, checked) {
      this.itemIds[item.id] = checked;
    },
    onPreviousPage() {
      this.page -= 1;
      this.getData(this.page);
    },
    onNextPage() {
      this.page += 1;
      this.getData(this.page);
    },
    getData(page = 1) {
      const { currentFilter, searchTerm, searchFieldName } = this;
      const { query = null, filters = [] } = currentFilter || {};
      const queryParams = {
        filter: {
          ...(query || filters.reduce((a, c) => ({ ...a, ...c }), {})),
          ...(searchTerm ? { [searchFieldName]: searchTerm } : {}),
        },
        page,
      };

      this.$emit('getData', queryParams);
    },
    setCustomFilter({ name = 'custom', filters = [] }) {
      if (!Array.isArray(filters) || filters.length === 0) {
        this.forgetFilter();
        return;
      }
      const indexOfFilter = this.filters.findIndex((fltr) => fltr.name === name);
      const newFilter = {
        name,
        filters: JSON.parse(JSON.stringify(filters)),
        custom: true,
      };
      if (indexOfFilter > -1) {
        this.filters.splice(indexOfFilter, 1, newFilter);
      } else {
        this.filters.push(newFilter);
      }
      this.setCurrentFilterAndUpdateList(newFilter);
    },
    forgetFilter(index) {
      this.filters.splice(index, 1);
      this.setCurrentFilterAndUpdateList({ ...baseFilters[0] });
      this.$emit('filters-cleared');
    },
  },
};
</script>
